import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixSocialGroupsV2GroupMember from './social-groups-v2-group-member-members.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A group member represents an existing site member that has joined a group.
 * You can add, remove, and query group members.
 * Learn more about [group members](https://support.wix.com/en/article/wix-groups-adding-new-members-to-your-group).
 */
export interface GroupMember {
  /**
   * Member ID. See the Members API for more details.
   * @readonly
   * @format GUID
   */
  memberId?: string;
  /** Group member role. */
  role?: GroupRole;
  /**
   * Date and time the group Member joined the group.
   * @readonly
   */
  joinedDate?: Date | null;
}

/**
 * A group member may have multiple roles in a single group.
 * Currently, only `MEMBER` and `ADMIN` roles are supported, but more roles may be available in the future.
 */
export interface GroupRole {
  /** Member's role. */
  value?: RoleWithLiterals;
}

export enum Role {
  /** Regular group member. */
  MEMBER = 'MEMBER',
  /** Group administrator. */
  ADMIN = 'ADMIN',
}

/** @enumType */
export type RoleWithLiterals = Role | 'MEMBER' | 'ADMIN';

export interface SocialGroupsEvent extends SocialGroupsEventPayloadOneOf {
  memberJoined?: MemberJoinedGroup;
  membersAdded?: MembersAddedToGroup;
  joinRequestsApproved?: JoinRequestsApproved;
  membersInvited?: MembersInvitedToGroup;
}

/** @oneof */
export interface SocialGroupsEventPayloadOneOf {
  memberJoined?: MemberJoinedGroup;
  membersAdded?: MembersAddedToGroup;
  joinRequestsApproved?: JoinRequestsApproved;
  membersInvited?: MembersInvitedToGroup;
}

export interface MemberJoinedGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberId?: string;
}

export interface MembersAddedToGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  whoAddedId?: string | null;
  /** @format GUID */
  siteMemberIds?: string[];
  /**
   * Used for Apes sticky experiment
   * @format GUID
   */
  operationId?: string;
}

export interface JoinRequestsApproved {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberIds?: string[];
  /**
   * Used for Apes sticky experiment
   * @format GUID
   */
  operationId?: string;
}

export interface MembersInvitedToGroup {
  /** @format GUID */
  groupId?: string;
  /** @format GUID */
  groupsInstanceId?: string;
  /** @format GUID */
  siteMemberIds?: string[];
}

/** ID of the group to join. */
export interface JoinRequest {
  /** @format GUID */
  groupId?: string;
  /**
   * Answers to membership questions. A Join Request will fail, if the answer to a required question is omitted.
   * @maxSize 50
   */
  membershipQuestionAnswers?: MembershipQuestionAnswer[];
  autoInviteId?: string | null;
}

/** Answer to a membership question. */
export interface MembershipQuestionAnswer {
  /**
   * Question ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Answer text.
   * @maxLength 300
   */
  text?: string | null;
}

export interface JoinResponse {
  /** New member. */
  member?: GroupMember;
}

export interface MemberJoined {
  /**
   * Group ID that member has joined.
   * @format GUID
   */
  groupId?: string;
  /** Joined group member. */
  groupMember?: GroupMember;
}

export enum JoinBy {
  MANUALLY = 'MANUALLY',
  PERSONAL_INVITE_LINK = 'PERSONAL_INVITE_LINK',
  PUBLIC_INVITE_LINK = 'PUBLIC_INVITE_LINK',
}

/** @enumType */
export type JoinByWithLiterals =
  | JoinBy
  | 'MANUALLY'
  | 'PERSONAL_INVITE_LINK'
  | 'PUBLIC_INVITE_LINK';

export interface LeaveRequest {
  /**
   * ID of the Group to leave.
   * @format GUID
   */
  groupId?: string;
}

export interface LeaveResponse {}

export interface MemberLeft {
  /**
   * Group ID that member has left.
   * @format GUID
   */
  groupId?: string;
  /** Group member that left. */
  groupMember?: GroupMember;
}

export interface AddGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Member IDs. See the Members API for details.
   * @format GUID
   * @maxSize 100
   */
  memberIds?: string[];
}

export interface AddGroupMembersResponse {
  /** New members. */
  members?: GroupMember[];
}

export interface MemberAdded {
  /**
   * Group ID that member was added to.
   * @format GUID
   */
  groupId?: string;
  /** Added group member. */
  groupMember?: GroupMember;
}

export interface RemoveGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Member IDs. See the Members API for details.
   * @format GUID
   * @maxSize 100
   */
  memberIds?: string[];
}

export interface RemoveGroupMembersResponse {}

export interface MemberRemoved {
  /**
   * Group ID that member was removed from.
   * @format GUID
   */
  groupId?: string;
  /** Removed group member. */
  groupMember?: GroupMember;
}

export interface ListGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of group members to skip in the list. */
  offset?: number | null;
}

export interface ListGroupMembersResponse {
  /** Retrieved members. */
  members?: GroupMember[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface QueryGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId: string;
  query?: Query;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryGroupMembersResponse {
  /** Group members. */
  members?: GroupMember[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface QueryNonGroupMembersRequest {
  /**
   * Group ID.
   * @format GUID
   */
  groupId?: string;
  query?: Query;
}

export interface QueryNonGroupMembersResponse {
  /** Retrieved members. */
  members?: GroupMember[];
}

export interface ListMembershipsRequest {
  /**
   * Member ID. See the Members API for details.
   * @format GUID
   */
  memberId: string | null;
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of memberships to skip in the list. */
  offset?: number | null;
}

export interface ListMembershipsResponse {
  /** Site member's memberships. */
  memberships?: Membership[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface Membership {
  /**
   * Group ID.
   * @readonly
   * @format GUID
   */
  groupId?: string;
  /** Membership status with group. */
  status?: MembershipStatusWithLiterals;
  /** Group member role. When membership status is not `JOINED`, this is empty. */
  role?: GroupRole;
}

export enum MembershipStatus {
  /** Undefined membership status. */
  UNKNOWN_STATUS = 'UNKNOWN_STATUS',
  /** Site member is a group member. */
  JOINED = 'JOINED',
  /** Site member has submitted a request to join this group. The request is still `PENDING`. */
  PENDING = 'PENDING',
}

/** @enumType */
export type MembershipStatusWithLiterals =
  | MembershipStatus
  | 'UNKNOWN_STATUS'
  | 'JOINED'
  | 'PENDING';

export interface QueryMembershipsRequest {
  /**
   * Member ID. See the Members API for details.
   * @format GUID
   */
  memberId: string | null;
  query?: Query;
}

export interface QueryMembershipsResponse {
  /** Site member's memberships. */
  memberships?: Membership[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface QueryMyMembershipRequest {
  query?: Query;
}

export interface QueryMyMembershipResponse {
  /** Site member's memberships. */
  memberships?: Membership[];
  /** Paging information. */
  metadata?: PagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface GroupMemberAddedEnvelope {
  data: MemberAdded;
  metadata: EventMetadata;
}

/**
 * Triggered when a new member is added to a group, either directly by an admin or when a Join Request is approved.
 * @webhook
 * @eventType wix.social_groups.v2.group_member_added
 * @serviceIdentifier wix.social.groups.api.v2.GroupMembersService
 * @slug added
 */
export declare function onGroupMemberAdded(
  handler: (event: GroupMemberAddedEnvelope) => void | Promise<void>
): void;

export interface GroupMemberRemovedEnvelope {
  data: MemberRemoved;
  metadata: EventMetadata;
}

/**
 * Triggered when a member is removed from a group.
 * @webhook
 * @eventType wix.social_groups.v2.group_member_removed
 * @serviceIdentifier wix.social.groups.api.v2.GroupMembersService
 * @slug removed
 */
export declare function onGroupMemberRemoved(
  handler: (event: GroupMemberRemovedEnvelope) => void | Promise<void>
): void;

/**
 * Add site members to a specific group. Public members are added right away, while private members receive an invitation to join the group.
 *
 * For `SECRET` groups, group admins and group members can add additional members to their group.
 * For `PUBLIC` and `PRIVATE` groups, only group admins can add additional members to their group.
 * They can also allow all group members to add a new member to the group with `group.settings`.
 * @param groupId - Group ID.
 * @param memberIds - Member IDs. See the Members API for details.
 * @public
 * @requiredField groupId
 * @requiredField memberIds
 * @fqn wix.social.groups.api.v2.GroupMembersService.AddGroupMembers
 */
export async function addGroupMembers(
  groupId: string,
  memberIds: string[]
): Promise<
  NonNullablePaths<
    AddGroupMembersResponse,
    `members` | `members.${number}.memberId` | `members.${number}.role.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    groupId: groupId,
    memberIds: memberIds,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupMember.addGroupMembers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { groupId: '$[0]', memberIds: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['groupId', 'memberIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Removes site members from a specific group.
 *
 * Only group admins can remove members from their group.
 * @param groupId - Group ID.
 * @param memberIds - Member IDs. See the Members API for details.
 * @public
 * @requiredField groupId
 * @requiredField memberIds
 * @fqn wix.social.groups.api.v2.GroupMembersService.RemoveGroupMembers
 */
export async function removeGroupMembers(
  groupId: string,
  memberIds: string[]
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    groupId: groupId,
    memberIds: memberIds,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupMember.removeGroupMembers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { groupId: '$[0]', memberIds: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['groupId', 'memberIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of up to 100 group members, given the provided paging.
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 *
 * Default sorts by `joinedDate` in descending order.
 * @param groupId - Group ID.
 * @public
 * @requiredField groupId
 * @param options - Paging options.
 * @fqn wix.social.groups.api.v2.GroupMembersService.ListGroupMembers
 */
export async function listGroupMembers(
  groupId: string,
  options?: ListGroupMembersOptions
): Promise<
  NonNullablePaths<
    ListGroupMembersResponse,
    `members` | `members.${number}.memberId` | `members.${number}.role.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    groupId: groupId,
    limit: options?.limit,
    offset: options?.offset,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupMember.listGroupMembers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          groupId: '$[0]',
          limit: '$[1].limit',
          offset: '$[1].offset',
        },
        singleArgumentUnchanged: false,
      },
      ['groupId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListGroupMembersOptions {
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of group members to skip in the list. */
  offset?: number | null;
}

/**
 * Retrieves a list of up to 100 group members, given the provided paging, sorting and filtering.
 *
 *
 * Creates a query to retrieve a list of group members.
 *
 * > **Note:** For `SECRET` groups, only group admins and group members can query group members. <!-- However, if the `suppressAuth` option is set to `true`, all permissions are overwritten, and all site members (including non-group members) can query group members.  -->
 *
 * The `queryGroupMembers()` function builds a query to retrieve a list of all group members, and returns a [GroupMembersQueryBuilder](#membersquerybuilder) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](#membersquerybuilder/find) function.
 *
 * You can refine the query by chaining `GroupMembersQueryBuilder` functions onto the query. `GroupMembersQueryBuilder` functions enable you to sort, filter, and control the results that `queryMembers()` returns.
 *
 *  `queryGroupMembers()` runs with these `GroupMembersQueryBuilder` defaults, which you can override:
 *  + [`limit(100)`](/members-query-builder/limit)
 *  + [`descending("joinedDate")`](/members-query-builder/descending)
 *
 * The following `GroupMembersQueryBuilder` functions are supported for `queryGroupMembers()`. For a full description of the Members object, see the object returned for the [`items`](/members-query-result/items) property in `MembersQueryResult`.
 * @public
 * @requiredField groupId
 * @param groupId - Group ID.
 * @fqn wix.social.groups.api.v2.GroupMembersService.QueryGroupMembers
 */
export function queryGroupMembers(groupId: string): MembersQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    GroupMember,
    'OFFSET',
    QueryGroupMembersRequest,
    QueryGroupMembersResponse
  >({
    func: async (payload: QueryGroupMembersRequest) => {
      const reqOpts =
        ambassadorWixSocialGroupsV2GroupMember.queryGroupMembers(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryGroupMembersRequest['query']) => {
      const args = [query, groupId, {}] as [
        QueryGroupMembersRequest['query'],
        typeof groupId,
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        groupId: args?.[1],
        ...args?.[2],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryGroupMembersResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.members,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { groupId: '$[1]', query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'OFFSET',
    transformationPaths: {},
  });
}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface MembersQueryResult extends QueryOffsetResult {
  items: GroupMember[];
  query: MembersQueryBuilder;
  next: () => Promise<MembersQueryResult>;
  prev: () => Promise<MembersQueryResult>;
}

export interface MembersQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (propertyName: 'role', value: any) => MembersQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (propertyName: 'role', value: any) => MembersQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (propertyName: 'role', value: any[]) => MembersQueryBuilder;
  in: (propertyName: 'role', value: any) => MembersQueryBuilder;
  exists: (propertyName: 'role', value: boolean) => MembersQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<'role' | 'joinedDate'>
  ) => MembersQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<'role' | 'joinedDate'>
  ) => MembersQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => MembersQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results. */
  skip: (skip: number) => MembersQueryBuilder;
  find: () => Promise<MembersQueryResult>;
}

/**
 * @hidden
 * @fqn wix.social.groups.api.v2.GroupMembersService.QueryGroupMembers
 * @requiredField query
 */
export async function typedQueryGroupMembers(
  query: GroupMemberQuery
): Promise<
  NonNullablePaths<
    QueryGroupMembersResponse,
    `members` | `members.${number}.memberId` | `members.${number}.role.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupMember.queryGroupMembers(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GroupMemberQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  GroupMember,
  GroupMemberQuerySpec
>;
export type GroupMemberQuery = {
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'];
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Paging options to limit and skip the number of items.  
  */
  paging?: {
    /** 
  Number of items to load.  
  */
    limit?: NonNullable<CommonQueryWithEntityContext['paging']>['limit'] | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonQueryWithEntityContext['paging']>['offset']
      | null;
  };
};

export const utils = {
  ...createQueryUtils<GroupMember, GroupMemberQuerySpec, GroupMemberQuery>(),
};

/**
 * Retrieves a list of group memberships of a site member.
 *
 * Only Wix users can retrieve a site member's group memberships.
 * Site members can only retrieve a list of their own group memberships.
 *
 * Default sorts by `groupId` in descending order.
 * @public
 * @requiredField memberId
 * @param memberId - Site member ID.
 * @param options - Paging options.
 * @fqn wix.social.groups.api.v2.GroupMembersService.ListMemberships
 */
export async function listMemberships(
  memberId: string,
  options?: ListMembershipsOptions
): Promise<
  NonNullablePaths<
    ListMembershipsResponse,
    | `memberships`
    | `memberships.${number}.groupId`
    | `memberships.${number}.status`
    | `memberships.${number}.role.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    memberId: memberId,
    limit: options?.limit,
    offset: options?.offset,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupMember.listMemberships(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          memberId: '$[0]',
          limit: '$[1].limit',
          offset: '$[1].offset',
        },
        singleArgumentUnchanged: false,
      },
      ['memberId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListMembershipsOptions {
  /**
   * Number of items to load. Maximum `100`.
   * @max 100
   */
  limit?: number | null;
  /** Number of memberships to skip in the list. */
  offset?: number | null;
}

/**
 * Retrieves a list of up to 100 members and their membership status, given the provided paging and filtering.
 *
 * The queryMemberships function returns a Promise that resolves to a list of memberships.
 *
 * >**Note:** Site members can only query their own memberships.
 *
 * | Property                    | Supported Filters & Sorting                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                            |
 * | --------------------------- | -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
 * | `status`                    | [`eq()`](#membershipsquerybuilder/eq), [`ne()`](#membershipsquerybuilder/ne)             |
 * | `role`                      | [`eq()`](#membershipsquerybuilder/eq), [`ne()`](#membershipsquerybuilder/ne)             |
 *
 * @public
 * @requiredField memberId
 * @param memberId - Site member ID.
 * @fqn wix.social.groups.api.v2.GroupMembersService.QueryMemberships
 */
export async function queryMemberships(
  memberId: string,
  options?: QueryMembershipsOptions
): Promise<
  NonNullablePaths<
    QueryMembershipsResponse,
    | `memberships`
    | `memberships.${number}.groupId`
    | `memberships.${number}.status`
    | `memberships.${number}.role.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    memberId: memberId,
    query: options?.query,
  });

  const reqOpts =
    ambassadorWixSocialGroupsV2GroupMember.queryMemberships(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { memberId: '$[0]', query: '$[1].query' },
        singleArgumentUnchanged: false,
      },
      ['memberId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryMembershipsOptions {
  query?: Query;
}
